/**
 * Safely parses a field that may be either a native array/object or a JSON-encoded string.
 *
 * The S3 site config sometimes serializes array/object fields as JSON strings
 * (e.g., footerLinks: "[{...}]" instead of footerLinks: [{...}]).
 * This utility handles both forms and returns undefined if the value is falsy,
 * not parseable, or not the expected type.
 *
 * @param value - The raw field value (array, object, JSON string, or undefined)
 * @returns The parsed value, or undefined if invalid
 */
export function parseJsonField<T>(value: T | string | undefined | null): T | undefined {
  if (value == null) return undefined;

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed ?? undefined;
    } catch {
      return undefined;
    }
  }

  return value as T;
}

/**
 * Parses a field that should be an array. Returns undefined if the result is not an array.
 */
export function parseArrayField<T>(value: T[] | string | undefined | null): T[] | undefined {
  const parsed = parseJsonField<T[]>(value);
  return Array.isArray(parsed) ? parsed : undefined;
}
